import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { useNavigate } from 'react-router';

import {
  ButtonField,
  ButtonFieldIcon,
  Dropdown,
  DropdownList,
  DropdownListItem,
  PillSuggestion,
  Tooltip,
  TooltipBody,
} from '@wk/components-react16';
import {
  IconAlertCircleFilled,
  IconArrowLeft,
  IconCheck,
  IconCircleCheckFilled,
  IconCircleMinus,
  IconCircleXFilled,
  IconColorFilter,
  IconDotsVertical,
  IconFilter,
  IconMenu2,
} from '@tabler/icons-react';

import { ENVIRONMENT_ACRONYMS, ORR_PLATFORMS } from '@/config/constants';
import { cn, userLocaleTimezone } from '@/lib/utils';
import { AutomationPlatform } from '@/types';
import type {
  Application,
  CheckpointDivision,
  CheckpointGroup,
  CheckpointItem,
  Evaluation,
  EvaluationCheckpoint,
} from '@/types';

import { type EvaluationView } from '../../lib/viewAccess';
import { getAccountableJiraBoards } from '@/domains/access/systemConfigApi';
import EvaluationSidebarDetailsPanel from './EvaluationSidebarDetailsPanel';
import EvaluationSidebarTree from './EvaluationSidebarTree';
import type {
  EvaluationSidebarFilter,
  EvaluationSidebarState,
} from './EvaluationSidebar.types';
import {
  buildVersionsByCheckpointId,
  computeCheckpointsByAccountable,
  getCheckpointKey,
  isCheckpointGroup,
  isCheckpointItem,
  MOBILE_MEDIA_QUERY,
} from './EvaluationSidebar.utils';

import './EvaluationSidebar.css';

interface EvaluationSidebarProps {
  evaluation: Evaluation | null;
  activeCheckpoint: EvaluationCheckpoint | null;
  onCheckpointClick: (checkpoint: EvaluationCheckpoint) => void;
  application: Application | null;
  activeView: EvaluationView;
  availableViews: EvaluationView[];
  hideEnvironmentBadge?: boolean;
  showApplicationDetails?: boolean;
  onBackClick?: () => void;
  onViewChange: (view: EvaluationView) => void;
}

const DESKTOP_FILTER_OPTIONS: Array<{
  value: EvaluationSidebarFilter;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'not-scored', label: 'Not Scored' },
  { value: 'error', label: 'Error' },
];

const EvaluationSidebar: FC<EvaluationSidebarProps> = ({
  evaluation,
  activeCheckpoint,
  onCheckpointClick,
  application,
  activeView,
  availableViews,
  hideEnvironmentBadge = false,
  showApplicationDetails = true,
  onBackClick,
  onViewChange,
}) => {
  // Accountable-team -> Jira board map is data-driven (GET /system/jira-boards),
  // shared with Report-a-Bug — never hardcoded here.
  const { data: accountableJiraBoards } = useQuery({
    queryKey: ['system', 'jira-boards'],
    queryFn: async () => (await getAccountableJiraBoards()).data,
    staleTime: Infinity,
  });

  const environment = useMemo(
    () => evaluation?.request?.environmentNames[0] ?? '',
    [evaluation],
  );

  const navigate = useNavigate();
  const canSwitchEvaluationView = availableViews.length > 1;

  const isPass = useMemo(() => {
    return Boolean(evaluation?.score && evaluation?.score >= 100);
  }, [evaluation]);

  const [state, setState] = useState<EvaluationSidebarState>({
    checkpointsByAccountable: null,
    isDetailsOpen: false,
    filter: 'all',
    isFilterOpen: false,
  });
  // Tracks the active checkpoint key we have already auto-expanded for.
  // Rebuilding the tree resets this guard; user-driven collapse does not.
  const autoExpandedForKey = useRef<string | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  });
  const [isMobileDetailsExpanded, setIsMobileDetailsExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  });

  const versionsByCheckpointId = useMemo(() => {
    return buildVersionsByCheckpointId(evaluation);
  }, [evaluation]);

  const handleStateUpdate = (newState: Partial<EvaluationSidebarState>) => {
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  const handleOpenMobileDetails = () => {
    handleStateUpdate({ isDetailsOpen: true });
  };

  const toggleNode = (accountableKey: string, checkpoint?: CheckpointItem) => {
    setState((prevState) => {
      const newState = cloneDeep(prevState);

      if (!newState.checkpointsByAccountable) return newState;

      const division = newState.checkpointsByAccountable[accountableKey];

      if (!checkpoint) {
        division.isActive = !division.isActive;
        return newState;
      }

      if (isCheckpointGroup(checkpoint)) {
        const group = division.checkpoints.find(
          (candidate): candidate is CheckpointGroup =>
            isCheckpointGroup(candidate) &&
            candidate.groupName === checkpoint.groupName,
        );

        if (group) {
          group.isActive = !group.isActive;
        }
      }

      return newState;
    });
  };

  useEffect(() => {
    if (evaluation) {
      autoExpandedForKey.current = null;
      handleStateUpdate({
        checkpointsByAccountable: computeCheckpointsByAccountable(
          evaluation,
          accountableJiraBoards,
        ),
      });
    }
    // accountableJiraBoards is in deps so bugKey resolves once the data-driven map loads.
  }, [evaluation, accountableJiraBoards]);

  useEffect(() => {
    if (!activeCheckpoint || !state.checkpointsByAccountable) return;

    const activeKey = getCheckpointKey(activeCheckpoint);
    if (autoExpandedForKey.current === activeKey) return;
    autoExpandedForKey.current = activeKey;

    Object.entries(state.checkpointsByAccountable).forEach(
      ([accountableKey, division]) => {
        division.checkpoints.forEach((item) => {
          if (isCheckpointGroup(item)) {
            const foundInGroup = item.checkpoints.some(
              (checkpoint) => getCheckpointKey(checkpoint) === activeKey,
            );

            if (foundInGroup && (!division.isActive || !item.isActive)) {
              setState((prevState) => {
                const updatedState = cloneDeep(prevState);

                if (updatedState.checkpointsByAccountable) {
                  updatedState.checkpointsByAccountable[
                    accountableKey
                  ].isActive = true;

                  const group = updatedState.checkpointsByAccountable[
                    accountableKey
                  ].checkpoints.find(
                    (candidate): candidate is CheckpointGroup =>
                      isCheckpointGroup(candidate) &&
                      candidate.groupName === item.groupName,
                  );

                  if (group) {
                    group.isActive = true;
                  }
                }

                return updatedState;
              });
            }

            return;
          }

          if (isCheckpointItem(item) && getCheckpointKey(item) === activeKey) {
            if (!division.isActive) {
              setState((prevState) => {
                const updatedState = cloneDeep(prevState);

                if (updatedState.checkpointsByAccountable) {
                  updatedState.checkpointsByAccountable[
                    accountableKey
                  ].isActive = true;
                }

                return updatedState;
              });
            }
          }
        });
      },
    );
  }, [activeCheckpoint, state.checkpointsByAccountable]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', onViewportChange);

    return () => {
      mediaQuery.removeEventListener('change', onViewportChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsMobileDetailsExpanded(true);
      setState((prevState) => ({ ...prevState, isFilterOpen: false }));
    }
  }, [isMobileViewport]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const openMobileDetails = () => {
      if (isMobileViewport) {
        handleStateUpdate({ isDetailsOpen: true });
      }
    };

    window.addEventListener(
      'evaluation-details:open-mobile-details',
      openMobileDetails,
    );

    return () => {
      window.removeEventListener(
        'evaluation-details:open-mobile-details',
        openMobileDetails,
      );
    };
  }, [isMobileViewport]);

  const filterFn = useCallback(
    (checkpoint: EvaluationCheckpoint) => {
      if (state.filter === 'all') return true;
      if (state.filter === 'passed') return checkpoint.status === 'PASS';
      if (state.filter === 'failed') return checkpoint.status === 'FAIL';
      if (state.filter === 'not-scored') return !checkpoint.metadata.isScored;
      if (state.filter === 'error') return checkpoint.status === 'ERROR';
      return false;
    },
    [state.filter],
  );

  const filteredCheckpoints = useMemo(() => {
    if (!state.checkpointsByAccountable) return {};

    const filtered: Record<string, CheckpointDivision> = {};

    Object.entries(state.checkpointsByAccountable).forEach(
      ([key, accountable]) => {
        const filteredDivisionCheckpoints = accountable.checkpoints.filter(
          (checkpoint) => {
            if (isCheckpointGroup(checkpoint)) {
              return checkpoint.checkpoints.filter(filterFn).length > 0;
            }

            if (isCheckpointItem(checkpoint)) {
              return filterFn(checkpoint);
            }

            return false;
          },
        );

        if (filteredDivisionCheckpoints.length > 0) {
          filtered[key] = {
            ...accountable,
            checkpoints: filteredDivisionCheckpoints.map((checkpoint) => {
              if (isCheckpointGroup(checkpoint)) {
                return {
                  ...checkpoint,
                  checkpoints: checkpoint.checkpoints.filter(filterFn),
                };
              }

              return checkpoint;
            }),
          };
        }
      },
    );

    return filtered;
  }, [filterFn, state.checkpointsByAccountable]);

  const source = evaluation?.request?.headers[
    'x-orr-automation-platform'
  ] as AutomationPlatform;

  const resolvedPlatform: AutomationPlatform =
    source && Object.prototype.hasOwnProperty.call(ORR_PLATFORMS, source)
      ? source
      : AutomationPlatform.UNKNOWN;

  const SourceIcon = ORR_PLATFORMS[resolvedPlatform].icon;
  const sourceLabel = ORR_PLATFORMS[resolvedPlatform].label;

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
      return;
    }

    navigate(
      `/applications/${evaluation?.metadata.bitId}?env=${evaluation?.request.environmentNames[0]}`,
    );
  };

  const handleCheckpointFilterSelection = (
    filter: EvaluationSidebarFilter,
  ) => {
    if (isMobileViewport) {
      handleStateUpdate({ filter, isFilterOpen: false });
      return;
    }

    handleStateUpdate({ filter });
  };

  const handleCheckpointSelection = (checkpoint: EvaluationCheckpoint) => {
    onCheckpointClick(checkpoint);

    if (isMobileViewport) {
      handleStateUpdate({ isDetailsOpen: false, isFilterOpen: false });
    }
  };

  const checkpointsTreeContent = (
    <EvaluationSidebarTree
      filteredCheckpoints={filteredCheckpoints}
      isLoading={!state.checkpointsByAccountable}
      activeCheckpoint={activeCheckpoint}
      versionsByCheckpointId={versionsByCheckpointId}
      onToggleNode={toggleNode}
      onCheckpointSelect={handleCheckpointSelection}
    />
  );

  return (
    <aside className="evaluationSidebar flex h-full min-w-0 w-96 flex-col max-sm:h-auto max-sm:w-full">
      <div className="flex items-center gap-3 border-b border-gray-200 px-3 py-1 max-sm:items-start">
        <ButtonFieldIcon>
          <button onClick={handleBackClick} className="!p-0">
            <IconArrowLeft />
          </button>
        </ButtonFieldIcon>
        <div className="flex min-w-0 flex-col">
          <h1 className="flex items-center gap-1 text-sm font-normal max-sm:flex-wrap">
            Evaluation Results for
            <span className="text-sm font-medium">
              {evaluation?.metadata.bitId}
            </span>
          </h1>
          <div className="flex items-center gap-2 max-sm:flex-wrap">
            {evaluation?.metadata.name &&
            evaluation.metadata.name !== evaluation.metadata.bitId ? (
              <span className="text-sm font-medium max-sm:break-words">
                {evaluation.metadata.name}
              </span>
            ) : null}
            <Tooltip position="top" targetSelector={`#${environment}`}>
              <TooltipBody slot="tooltipBody">{environment}</TooltipBody>
            </Tooltip>
            {!hideEnvironmentBadge ? (
              <span
                id={environment}
                className={cn([
                  'envBadge',
                  `envBadge--${environment.toLowerCase()}`,
                ])}
              >
                <PillSuggestion size="small" key={environment}>
                  {
                    ENVIRONMENT_ACRONYMS[
                      environment.toLowerCase() as keyof typeof ENVIRONMENT_ACRONYMS
                    ]
                  }
                </PillSuggestion>
              </span>
            ) : null}
          </div>
        </div>
        {isMobileViewport ? (
          <div className="ml-auto mt-1 flex items-center gap-1">
            <button
              type="button"
              className="cursor-pointer"
              aria-label="Open checkpoints list"
              onClick={handleOpenMobileDetails}
            >
              <IconMenu2 size={20} />
            </button>
            <button
              type="button"
              id="evaluation-mobile-actions-trigger"
              className="cursor-pointer"
              aria-label="Open checkpoint actions"
              onClick={() => {
                if (typeof window === 'undefined') return;

                window.dispatchEvent(
                  new CustomEvent('evaluation:open-checkpoint-actions'),
                );
              }}
            >
              <IconDotsVertical size={20} />
            </button>
          </div>
        ) : null}
      </div>

      {!isMobileViewport ? (
        <div className="flex flex-col gap-2 p-3 pb-0">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-sm:grid-cols-1">
            <div className="flex flex-col gap-1">
              <h4 className="text-xs font-medium">Score</h4>
              <p
                className={cn([
                  'text-xs font-semibold',
                  isPass ? 'text-green-500' : 'text-red-500',
                ])}
              >
                {evaluation?.score}%
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="text-xs font-medium">Generated at</h4>
              <p className="text-xs">
                {format(new Date(`${evaluation?.generatedAt}`), 'yyyy-MM-dd HH:mm')}{' '}
                {userLocaleTimezone()}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="text-xs font-medium">Generated by</h4>
              <p className="text-xs">{evaluation?.generatedBy}</p>
            </div>
            {showApplicationDetails ? (
              <>
                <div className="flex flex-col gap-1">
                  <h4 className="text-xs font-medium">Managed by</h4>
                  <p className="text-xs">{application?.appOps ?? 'N/A'}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <h4 className="text-xs font-medium">Hosting model</h4>
                  <p className="text-xs">
                    {application?.hostingModel ?? 'N/A'}
                  </p>
                </div>
              </>
            ) : null}
            {showApplicationDetails ? (
              <div className="flex flex-col gap-1">
                <h4 className="text-xs font-medium">
                  All Accounts in WK Tenant?
                </h4>
                <p className="text-xs">
                  {evaluation?.isWKTenant != null
                    ? evaluation.isWKTenant
                      ? 'Yes'
                      : 'No'
                    : 'N/A'}
                </p>
              </div>
            ) : null}
          </div>
          <ButtonField mode="text" size="small">
            <button
              type="button"
              className="!p-0 !text-xs"
              onClick={() => handleStateUpdate({ isDetailsOpen: true })}
            >
              View other fields
            </button>
          </ButtonField>
        </div>
      ) : null}

      {!isMobileViewport ? (
        <>
          <div className="evaluationSidebar__checkpointsHeader flex items-center justify-between gap-2 px-3 py-2">
            <p className="text-sm font-medium">Checkpoints</p>
            <span className="flex gap-4">
              <span>
                <button
                  className="cursor-pointer"
                  id="filter-button-desktop"
                  onClick={() =>
                    handleStateUpdate({ isFilterOpen: !state.isFilterOpen })
                  }
                >
                  <IconFilter size={18} />
                </button>
                <Dropdown
                  targetSelector="#filter-button-desktop"
                  controlMode="controlled"
                  menuMode="application"
                  srLabelledby="filter-button-desktop"
                  isOpen={state.isFilterOpen}
                  onUserRequest={() =>
                    handleStateUpdate({ isFilterOpen: !state.isFilterOpen })
                  }
                >
                  <DropdownList>
                    {DESKTOP_FILTER_OPTIONS.map((filterOption) => (
                      <DropdownListItem
                        key={filterOption.value}
                        onItemClick={() =>
                          handleCheckpointFilterSelection(filterOption.value)
                        }
                        slot="dropdownListItem"
                      >
                        <span className="flex items-center gap-2">
                          <IconCheck
                            className={cn([
                              'invisible text-green-500',
                              {
                                visible: state.filter === filterOption.value,
                              },
                            ])}
                            size={18}
                          />
                          {filterOption.label}
                        </span>
                      </DropdownListItem>
                    ))}
                  </DropdownList>
                </Dropdown>
              </span>

              <span>
                <button id="icon-legend-desktop">
                  <IconColorFilter size={18} />
                </button>
                <Tooltip targetSelector="#icon-legend-desktop">
                  <TooltipBody slot="tooltipBody">
                    <div className="flex flex-col gap-2 p-1">
                      <span className="flex items-center gap-2">
                        <IconCircleCheckFilled
                          className="text-green-500"
                          size={18}
                        />
                        <span>Passed</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <IconCircleXFilled className="text-red-500" size={18} />
                        <span>Failed</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <IconCircleMinus
                          className="text-gray-400 [&>path:first-child]:stroke-gray-400 [&>path:last-child]:stroke-white"
                          size={18}
                        />
                        <span>Not Scored</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <IconAlertCircleFilled
                          className="text-red-500"
                          size={18}
                        />
                        <span>Error</span>
                      </span>
                    </div>
                  </TooltipBody>
                </Tooltip>
              </span>
            </span>
          </div>
          <div
            id="evaluation-checkpoints-tree"
            className="evaluationSidebar__tree flex-1 min-h-0 w-full overflow-y-auto px-3"
          >
            {checkpointsTreeContent}
          </div>
        </>
      ) : null}

      <EvaluationSidebarDetailsPanel
        isOpen={state.isDetailsOpen}
        evaluation={evaluation}
        application={application}
        showApplicationDetails={showApplicationDetails}
        isPass={isPass}
        isMobileViewport={isMobileViewport}
        isMobileDetailsExpanded={isMobileDetailsExpanded}
        onToggleMobileDetailsExpanded={() =>
          setIsMobileDetailsExpanded((previous) => !previous)
        }
        onClose={() =>
          handleStateUpdate({ isDetailsOpen: false, isFilterOpen: false })
        }
        canSwitchEvaluationView={canSwitchEvaluationView}
        availableViews={availableViews}
        activeView={activeView}
        onViewChange={onViewChange}
        filter={state.filter}
        onFilterSelect={handleCheckpointFilterSelection}
        checkpointsTreeContent={checkpointsTreeContent}
        sourceLabel={sourceLabel}
        SourceIcon={SourceIcon}
      />
    </aside>
  );
};

export default EvaluationSidebar;
